import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../core/common/api';

function ResetPasswordComponent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    meetsRequirements: false
  });

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid or missing reset token');
        setLoading(false);
        return;
      }

      try {
        const response = await api.call.get(`users/reset-password/validate/${token}`);
        
        if (response.data.isValid) {
          setTokenValid(true);
          setUserEmail(response.data.userEmail);
        } else {
          setError(response.data.message || 'Invalid or expired reset token');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Error validating reset token. Please request a new password reset.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Password strength checker. The first four checks mirror the API's Identity
  // password policy (Startup.cs) exactly — all four must pass before submitting.
  // The special-character check is scored but not required, since the API doesn't
  // require it either.
  const checkPasswordStrength = (password) => {
    let score = 0;
    const feedback = [];

    const hasMinLength = password.length >= 10;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecialChar = /[^a-zA-Z0-9]/.test(password);

    if (hasMinLength) {
      score += 1;
    } else {
      feedback.push('At least 10 characters');
    }

    if (hasLowercase) {
      score += 1;
    } else {
      feedback.push('At least one lowercase letter');
    }

    if (hasUppercase) {
      score += 1;
    } else {
      feedback.push('At least one uppercase letter');
    }

    if (hasDigit) {
      score += 1;
    } else {
      feedback.push('At least one number');
    }

    if (hasSpecialChar) {
      score += 1;
    } else {
      feedback.push('At least one special character');
    }

    const meetsRequirements = hasMinLength && hasLowercase && hasUppercase && hasDigit;

    return { score, feedback, meetsRequirements };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData(prev => ({ ...prev, newPassword }));
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const handleConfirmPasswordChange = (e) => {
    setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
  };

  const getPasswordStrengthColor = (score) => {
    if (score <= 2) return 'error';
    if (score <= 3) return 'warning';
    return 'success';
  };

  const getPasswordStrengthText = (score) => {
    if (score <= 2) return 'Weak';
    if (score <= 3) return 'Medium';
    return 'Strong';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validation
    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      setSubmitting(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setSubmitting(false);
      return;
    }

    if (!passwordStrength.meetsRequirements) {
      setError('Password must be at least 10 characters and include an uppercase letter, a lowercase letter, and a number.');
      setSubmitting(false);
      return;
    }

    try {
      const response = await api.call.post('users/reset-password', {
        token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('An error occurred while resetting your password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Validating reset token...</Typography>
        </Box>
      </Container>
    );
  }

  if (!tokenValid) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
              <span style={{ color: '#000' }}><em>Fast</em></span>
              <span style={{ color: '#9e2828' }}>Track</span>
            </Typography>
            
            <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
              {error}
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
              The password reset link may have expired or been used already. 
              Please request a new password reset.
            </Typography>
            
            <Button
              component={Link}
              to="/forgot-password"
              fullWidth
              variant="contained"
              sx={{ 
                mb: 2, 
                bgcolor: '#9e2828', 
                '&:hover': { bgcolor: '#7e2020' } 
              }}
            >
              Request New Reset Link
            </Button>
            
            <Button
              fullWidth
              variant="outlined"
              onClick={handleBackToLogin}
            >
              Back to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
              <span style={{ color: '#000' }}><em>Fast</em></span>
              <span style={{ color: '#9e2828' }}>Track</span>
            </Typography>
            
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              Password Reset Successful
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              Your password has been successfully reset. You can now log in with your new password.
            </Typography>
            
            <Button
              fullWidth
              variant="contained"
              onClick={handleBackToLogin}
              sx={{ 
                bgcolor: '#9e2828', 
                '&:hover': { bgcolor: '#7e2020' } 
              }}
            >
              Go to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
            <span style={{ color: '#000' }}><em>Fast</em></span>
            <span style={{ color: '#9e2828' }}>Track</span>
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 2 }}>
            Reset Password
          </Typography>
          
          {userEmail && (
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              Resetting password for: <strong>{userEmail}</strong>
            </Typography>
          )}
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              id="newPassword"
              autoComplete="new-password"
              value={formData.newPassword}
              onChange={handlePasswordChange}
              disabled={submitting}
            />
            
            {formData.newPassword && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Password Strength: {getPasswordStrengthText(passwordStrength.score)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(passwordStrength.score / 5) * 100}
                  color={getPasswordStrengthColor(passwordStrength.score)}
                  sx={{ height: 8, borderRadius: 5 }}
                />
                {passwordStrength.feedback.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Requirements: {passwordStrength.feedback.join(', ')}
                  </Typography>
                )}
              </Box>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleConfirmPasswordChange}
              disabled={submitting}
              error={formData.confirmPassword && formData.newPassword !== formData.confirmPassword}
              helperText={
                formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                bgcolor: '#9e2828', 
                '&:hover': { bgcolor: '#7e2020' } 
              }}
              disabled={submitting || !passwordStrength.meetsRequirements}
            >
              {submitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
            
            <Button
              fullWidth
              variant="text"
              onClick={handleBackToLogin}
              disabled={submitting}
            >
              Back to Login
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default ResetPasswordComponent;